<script lang="ts">
  import { Label, Input, Textarea, Checkbox, Card, Tabs, TabItem, Toggle } from "flowbite-svelte";
  import Icon from "@iconify/svelte";
  import TipTapEditor from "$lib/admin/components/content/TipTapEditor/TipTapEditor.svelte";
  import DynamicInputList from "$lib/admin/components/DynamicInputList.svelte";
  import type { IntroProps } from "./types";

  export let props: IntroProps;
  
  function addRecentChange() {
    props.recentChangesList = [...props.recentChangesList, ""];
  }
  
  function removeRecentChange(index: number) {
    const newList = [...props.recentChangesList];
    newList.splice(index, 1);
    props.recentChangesList = newList;
  }
  
  function editRecentChange(index: number, value: string) {
    const newList = [...props.recentChangesList];
    newList[index] = value;
    props.recentChangesList = newList;
  }
</script>

<div class="space-y-6 p-4 bg-white rounded-lg shadow">
  <!-- Heading Section -->
  <div>
    <Label for="header" class="mb-2 text-lg font-medium">Heading</Label>
    <Input id="header" bind:value={props.header} placeholder="Write an H1" on:keyup size="lg" class="w-full" />
  </div>

  <!-- Tabbed Content -->
  <Tabs style="underline" class="mt-6">
    <!-- Introduction Tab -->
    <TabItem open title="Introduction">
      <div class="p-4">
        <TipTapEditor placeholder="Introduction" bind:text={props.content} on:keyup />
      </div>
    </TabItem>

    <!-- Methodology Tab -->
    <TabItem title="Methodology">
      <div class="p-4">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium">Methodology Content</h3>
          <div class="flex items-center gap-2">
            <Toggle bind:checked={props.methodology} size="small" />
            <span class="text-sm font-medium">Enabled</span>
          </div>
        </div>

        {#if props.methodology}
          <TipTapEditor bind:text={props.methodologyText} on:keyup />
        {:else}
          <div class="py-8 text-center text-gray-500 italic bg-gray-50 rounded-lg">
            <Icon icon="heroicons:information-circle" class="w-8 h-8 mx-auto mb-2" />
            <p>Enable this section to add methodology content</p>
          </div>
        {/if}
      </div>
    </TabItem>

    <!-- Recent Changes Tab -->
    <TabItem title="Recent Changes">
      <div class="p-4">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium">Recent Changes</h3>
          <div class="flex items-center gap-2">
            <Toggle bind:checked={props.recentChanges} size="small" />
            <span class="text-sm font-medium">Enabled</span>
          </div>
        </div>

        {#if props.recentChanges}
          <DynamicInputList
            items={props.recentChangesList}
            label="Changes"
            onAdd={addRecentChange}
            onRemove={removeRecentChange}
            onEdit={editRecentChange}
            placeholder="Enter a recent change"
            color="blue"
            icon="heroicons:clock"
            richEditor={true}
            compactMode={true}
          />
        {:else}
          <div class="py-8 text-center text-gray-500 italic bg-gray-50 rounded-lg">
            <Icon icon="heroicons:information-circle" class="w-8 h-8 mx-auto mb-2" />
            <p>Enable this section to add recent changes</p>
          </div>
        {/if}
      </div>
    </TabItem>

    <!-- CRM Tab -->
    <TabItem title="CRM">
      <div class="p-4">
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium">CRM Information</h3>
          <div class="flex items-center gap-2">
            <Toggle bind:checked={props.crm} size="small" />
            <span class="text-sm font-medium">Enabled</span>
          </div>
        </div>

        {#if props.crm}
          <div class="mt-2">
            <Label class="mb-2">CRM Text</Label>
            <TipTapEditor bind:text={props.crmText} placeholder="Enter CRM text" on:keyup />
          </div>
        {:else}
          <div class="py-8 text-center text-gray-500 italic bg-gray-50 rounded-lg">
            <Icon icon="heroicons:information-circle" class="w-8 h-8 mx-auto mb-2" />
            <p>Enable this section to add CRM information</p>
          </div>
        {/if}
      </div>
    </TabItem>
  </Tabs>
</div>
